import { Campaign, Collab, computeContentDueDate, draftPhaseDone, getPostByDeadline as getPostByDeadlineInTz, goLiveKind, isPostByCampaign, PRE_ACCEPT_STATUSES, preAcceptExtendedDueDate, resolveTurnaroundDays, shippingClockStart, shippingGraceApplies } from '@foodfluence/shared';
import { getBrowserTimezone } from 'utils/timezoneUtils';
import { toDate } from '../../../utils/dateUtils';
import { isLastContentSubmitted } from 'utils/deliverableUtils';
import { requiresBrandReview } from 'utils/campaignUtils';
import { isBrandManagedCampaignType, isInStoreCampaignType, isRetailCampaignType, isShopifyCampaignType, isShipToHomeCampaignType } from 'utils/collabUtils';

/**
 * End of the go-live window (or of the late-approval posting day), or null without go-live (shared
 * rule, see getPostByDeadline in @foodfluence/shared). `timezone` defaults to the browser's; the
 * server checks against the store's/creator's.
 */
export const getPostByDeadline = (
  collab: Collab | null | undefined,
  campaign: Campaign | null | undefined,
  timezone: string = getBrowserTimezone()
): Date | null => getPostByDeadlineInTz(collab, campaign, timezone);

/**
 * The go-live deadline when it is the one the creator works to: a go-live collab with no draft
 * owed (post collab, or every draft approved). Null otherwise, including every collab without
 * go-live — those keep contentDueDate.
 */
export const getGoLivePostingDeadline = (
  collab: Collab,
  campaign: Campaign | null | undefined,
  timezone: string = getBrowserTimezone()
): Date | null => {
  if (!isPostByCampaign(collab)) return null;
  if (goLiveKind(campaign, collab) !== 'post' && !draftPhaseDone(collab)) return null;
  return getPostByDeadlineInTz(collab, campaign, timezone);
};

/**
 * Days-after: a brand-approved extension the visit write keeps instead of `visitDue` (visit +
 * turnaround), the same LATER rule as the backend's applyInStoreVisitSchedule. Before acceptance
 * the extension is clamped from acceptance (`now`) as turnaroundLockFields does; on a locked
 * collab it is the stored date marked contentDueDateExtendedAt. Null when there is none or it is
 * not later than `visitDue`: the due date follows the visit.
 */
export const getAcceptKeptDueDate = (
  collab: Collab | null | undefined,
  campaign: Campaign | null | undefined,
  timezone: string,
  visitDue: Date,
  now: Date = new Date()
): Date | null => {
  if (!collab || !campaign) return null;
  const kept = collab.contentTurnaroundDays != null
    ? (collab.contentDueDateExtendedAt ? toDate(collab.contentDueDate) : null)
    : PRE_ACCEPT_STATUSES.includes(collab.status)
      ? preAcceptExtendedDueDate(collab, computeContentDueDate(now, resolveTurnaroundDays(campaign), 0, timezone), timezone)
      : null;
  return kept && kept.getTime() > visitDue.getTime() ? kept : null;
};

/**
 * `timezone` is the collab's due-date timezone (useDueDateTimezone); it only matters for the
 * ship-to-home fallback, where it picks the shipping day and its end, as the server stamp does.
 */
export const getContentDueDateRaw = (
  collab: Collab,
  campaign: Campaign,
  timezone: string = getBrowserTimezone()
): Date | null => {
  // Go-live: once no draft is owed, the creator works to go-live end, not the draft/turnaround clock.
  const goLive = getGoLivePostingDeadline(collab, campaign, timezone);
  if (goLive) return goLive;
  const explicitDueDate = toDate(collab.contentDueDate);
  if (explicitDueDate) {
    return explicitDueDate;
  }

  if (isInStoreCampaignType(campaign?.campaignType)) {
    if (collab?.scheduledCollabDate) {
      const visitDate = toDate(collab.scheduledCollabDate);
      if (visitDate) {
        const dueDate = new Date(visitDate);
        dueDate.setHours(23, 59, 59, 999);
        dueDate.setDate(dueDate.getDate() + 7);
        return dueDate;
      }
    }
  } else if (isRetailCampaignType(campaign?.campaignType)) {
    const confirmedTime = toDate(collab.collabConfirmedTime);
    if (confirmedTime) {
      const dueDate = new Date(confirmedTime);
      dueDate.setHours(23, 59, 59, 999);
      dueDate.setDate(dueDate.getDate() + 13);
      return dueDate;
    }
  } else if (isShopifyCampaignType(campaign?.campaignType)) {
    const orderFulfilledTime = toDate(collab.orderFulfilledTime);
    if (orderFulfilledTime) {
      const dueDate = new Date(orderFulfilledTime);
      dueDate.setHours(23, 59, 59, 999);
      dueDate.setDate(dueDate.getDate() + 13);
      return dueDate;
    }
  } else if (isShipToHomeCampaignType(campaign?.campaignType)) {
    // No stored date yet (a due date is stamped when shipping is confirmed or the creator
    // reports receipt) — derive it from shipping so a collab that predates the stamp still counts.
    // Same start the server stamps: under 7 days, receipt or shipped + 4 days (shippingClockStart).
    const start = shippingClockStart(
      toDate(collab?.shippingConfirmed),
      toDate(collab?.productReceivedTime),
      shippingGraceApplies('shipToHome', collab?.contentTurnaroundDays)
    );
    if (start && collab?.contentTurnaroundDays) {
      return computeContentDueDate(
        start,
        collab.contentTurnaroundDays,
        collab.contentDueDateExtensionDays ?? 0,
        timezone
      );
    }
  }
  return null;
};

export const formatTimeLeft = (
  targetDate: Date | null,
  futureLabel: string = 'Left to Submit Content',
  pastLabel: string = 'Late to Submit Content'
): string | null => {
  if (!targetDate) return null;
  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();
  const absDiffMs = Math.abs(diffMs);
  const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((absDiffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const timePart = days > 0 ? `${days}D ${hours}H` : `${hours}H`;
  return diffMs >= 0
    ? `${timePart} ${futureLabel}`
    : `${timePart} ${pastLabel}`;
};

export const computeDaysLeft = (targetDate: Date | null): number | null => {
  if (!targetDate) return null;
  const now = new Date();
  return Math.ceil((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

export const formatDate = (date: any) => {
    if (date && date.toDate instanceof Function) {
      return date.toDate();
    }
    return null;
};

/**
 * Determines if the content due reminder should be shown for a collab.
 * Currently only shows for in-store collabs with a scheduled visit date.
 * Can be extended to other campaign types in the future.
 */
export const shouldShowContentDueReminder = (
  collab: Collab,
  campaign: Campaign
): boolean => {

  const isInStore = isInStoreCampaignType(campaign?.campaignType);
  const isRetail = isRetailCampaignType(campaign?.campaignType);
  const isShopify = isShopifyCampaignType(campaign?.campaignType);
  const isBrandManaged = isBrandManagedCampaignType(campaign?.campaignType);
  const isShipToHome = isShipToHomeCampaignType(campaign?.campaignType);

  if (!isInStore && !isRetail && !isShopify && !isBrandManaged && !isShipToHome) {
    return false;
  }

  // Go-live: once the go-live deadline is the one owed (no draft left), the live post gates it, so
  // the "Post by" countdown keeps showing after draft approval.
  const draftGate = !getGoLivePostingDeadline(collab, campaign) && requiresBrandReview(campaign, collab);
  if (isLastContentSubmitted(collab, draftGate) || !displayCollabSubmissionReminder(collab, campaign)) {
    return false;
  }

  return true;
};


export const shouldShowContentDueReminderBeforeAcceptance = (collab: Collab, campaign: Campaign): boolean => {
  const isInStore = isInStoreCampaignType(campaign?.campaignType);
  const isRetail = isRetailCampaignType(campaign?.campaignType);

  if (!collab || (!isInStore && !isRetail)) {
    return false;
  }
  
  return collab.status === 'pendingInfluencerApproval';
};

/**
 * Formats the content due text based on days remaining.
 * Returns "Due in X days", "Due today", or "X days overdue"
 */
export const formatContentDueText = (daysUntilDue: number): string => {
  if (daysUntilDue < 0) {
    const daysOverdue = Math.abs(daysUntilDue);
    return `${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'} overdue`;
  } else if (daysUntilDue === 0) {
    return 'Due today';
  } else {
    return `Due in ${daysUntilDue} ${daysUntilDue === 1 ? 'day' : 'days'}`;
  }
};

/**
 * Checks if the visit date for an in-store collab has passed.
 * Content due reminders should only show AFTER the visit has occurred.
 */
export const displayCollabSubmissionReminder = (collab: Collab, campaign: Campaign): boolean => {
  const isInStore = isInStoreCampaignType(campaign?.campaignType);
  const isRetail = isRetailCampaignType(campaign?.campaignType);
  const isShopify = isShopifyCampaignType(campaign?.campaignType);
  const isBrandManaged = isBrandManagedCampaignType(campaign?.campaignType);
  const isShipToHome = isShipToHomeCampaignType(campaign?.campaignType);

  if (!isInStore && !isRetail && !isShopify && !isBrandManaged && !isShipToHome) {
    return false;
  }

  if (isInStore) {
    if (!collab?.scheduledCollabDate) {
      return false;
    }
  
    const visitDate = toDate(collab.scheduledCollabDate);
    if (!visitDate) {
      return false;
    }
    
    const today = new Date();
    return visitDate <= today || collab.status === 'pendingSubmission'; // Visit date is in the past
  }
  else if (isRetail) {
    return !!collab?.scheduledPostingDate;
  }
  else if (isShopify) {
    // Go-live post collabs: the deadline is fixed at acceptance, so show it before the order ships
    // — but not before acceptance, where the 72h response countdown applies.
    return !!collab?.orderFulfilledTime || collab.status === 'pendingSubmission' ||
      (!!getGoLivePostingDeadline(collab, campaign) && collab.status !== 'pendingInfluencerApproval');
  }
  else if (isBrandManaged) {
    return !!collab?.contentDueDate;
  }
  else if (isShipToHome) {
    return !!getContentDueDateRaw(collab, campaign);
  }
  return false;
};
